import { useEffect, useState } from "react";
import type { EnvironmentId, OrchestrationThreadSearchMatch } from "@cmmd-forge/contracts";

import { readEnvironmentApi } from "~/environmentApi";
import { threadSearchMatchKey } from "./sidebarV2Search";

export type SidebarContentMatch = OrchestrationThreadSearchMatch & {
  readonly environmentId: EnvironmentId;
};

const EMPTY_MATCHES: ReadonlyMap<string, SidebarContentMatch> = new Map();
const SEARCH_DEBOUNCE_MS = 200;

/** Search each connected environment; discard responses for an older query or unmounted sidebar. */
export function useSidebarV2ContentSearch(
  environmentIds: readonly EnvironmentId[],
  query: string,
): { readonly matches: ReadonlyMap<string, SidebarContentMatch>; readonly isPending: boolean } {
  const normalizedQuery = query.trim();
  const environmentKey = JSON.stringify([...new Set(environmentIds)].toSorted());
  const searchKey = JSON.stringify([environmentKey, normalizedQuery]);
  const canSearch = normalizedQuery.length >= 2 && normalizedQuery.length <= 200;
  const [result, setResult] = useState<{
    readonly key: string;
    readonly matches: ReadonlyMap<string, SidebarContentMatch>;
  } | null>(null);

  useEffect(() => {
    if (!canSearch) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      const ids = JSON.parse(environmentKey) as EnvironmentId[];
      const searches = ids.map(async (environmentId) => {
        const api = readEnvironmentApi(environmentId);
        if (!api) return [];
        const response = await api.orchestration.searchThreads({ query: normalizedQuery });
        const matches: SidebarContentMatch[] = [];
        for (const match of response.matches) matches.push({ ...match, environmentId });
        return matches;
      });
      void Promise.allSettled(searches).then((outcomes) => {
        if (cancelled) return;
        const matches = new Map<string, SidebarContentMatch>();
        for (const outcome of outcomes) {
          if (outcome.status !== "fulfilled") continue;
          for (const match of outcome.value) {
            matches.set(threadSearchMatchKey(match), match);
          }
        }
        setResult({ key: searchKey, matches });
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [canSearch, environmentKey, normalizedQuery, searchKey]);

  return {
    matches: canSearch && result?.key === searchKey ? result.matches : EMPTY_MATCHES,
    isPending: canSearch && result?.key !== searchKey,
  };
}
